import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import walletService from '../services/walletService';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const WalletDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [balance, setBalance] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState({ all: [], recent: [], total: 0 });
  const [packages, setPackages] = useState({ all: [], total: 0, active: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    suspension_reason: ''
  });
  const [updateError, setUpdateError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [expandedPackages, setExpandedPackages] = useState(new Set());
  const [showAllLedger, setShowAllLedger] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    walletService.getWalletDetailsByUserId(token, id)
      .then((walletData) => {
        try {
          console.log('Wallet data received:', walletData);
          
          // Parse the new response structure
          // { wallet, balance, statistics, ledgerEntries, packages }
          const apiWallet = walletData?.wallet || {};
          const balanceData = walletData?.balance || {};
          const statsData = walletData?.statistics || {};
          const ledgerData = walletData?.ledgerEntries || {};
          const packagesData = walletData?.packages || {};
          
          // Extract wallet info
          const walletInfo = {
            id: apiWallet?.id || null,
            user_id: id,
            status: apiWallet?.status || apiWallet?.status || 'unknown',
            created_at: apiWallet?.createdAt || apiWallet?.created_at || null,
            suspended_at: apiWallet?.suspendedAt || apiWallet?.suspended_at || null,
            suspension_reason: apiWallet?.suspensionReason || apiWallet?.suspension_reason || null,
          };
          
          // Extract balance
          const balanceValue = typeof balanceData === 'object' && balanceData !== null
            ? (balanceData.balance || balanceData.amount || balanceData.total || 0)
            : (balanceData || 0);
          
          // Extract statistics
          const stats = {
            totalLedgerEntries: statsData?.totalLedgerEntries || 0,
            totalCredit: statsData?.totalCredit || 0,
            totalDebit: statsData?.totalDebit || 0,
            totalPackages: statsData?.totalPackages || 0,
            activePackages: statsData?.activePackages || 0,
            expiredPackages: statsData?.expiredPackages || 0,
            totalPackageBalance: statsData?.totalPackageBalance || 0,
          };
          
          // Extract ledger entries
          const ledger = {
            all: Array.isArray(ledgerData?.all) ? ledgerData.all : [],
            recent: Array.isArray(ledgerData?.recent) ? ledgerData.recent : [],
            total: ledgerData?.total || 0,
          };
          
          // Extract packages
          const userPackages = {
            all: Array.isArray(packagesData?.all) ? packagesData.all : [],
            total: packagesData?.total || 0,
            active: packagesData?.active || 0,
            expired: packagesData?.expired || 0,
          };
          
          if (!walletInfo.id) {
            throw new Error('Wallet data is missing required fields (wallet ID not found)');
          }
          
          setWallet(walletInfo);
          setBalance(balanceValue);
          setStatistics(stats);
          setLedgerEntries(ledger);
          setPackages(userPackages);
          setEditForm({
            status: walletInfo.status || '',
            suspension_reason: walletInfo.suspension_reason || ''
          });
        } catch (parseError) {
          console.error('Error parsing wallet data:', parseError, walletData);
          setError(new Error(`Failed to parse wallet data: ${parseError.message}`));
        }
      })
      .catch((err) => {
        console.error('Error fetching wallet details:', err);
        const errorMessage = err.response?.data?.error 
          || err.response?.data?.message 
          || err.message 
          || 'Error loading wallet details';
        setError(new Error(errorMessage));
      })
      .finally(() => setLoading(false));
  }, [id, token]);

  const handleEdit = () => {
    setIsEditing(true);
    setUpdateError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      status: wallet.status || '',
      suspension_reason: wallet.suspension_reason || ''
    });
    setUpdateError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setUpdateError(null);

    try {
      const updates = {
        status: editForm.status,
        suspension_reason: editForm.status === 'suspended' ? editForm.suspension_reason : null
      };

      if (!wallet || !wallet.id) {
        throw new Error('Wallet ID not available');
      }

      const updatedWallet = await walletService.updateWallet(token, wallet.id, updates);
      setWallet({ ...wallet, ...updatedWallet });
      setIsEditing(false);
    } catch (err) {
      setUpdateError(err.response?.data?.message || err.message || 'Failed to update wallet');
    } finally {
      setUpdating(false);
    }
  };

  const togglePackageExpansion = (packageId) => {
    setExpandedPackages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packageId)) {
        newSet.delete(packageId);
      } else {
        newSet.add(packageId);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    return `SAR ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="px-2 sm:px-0">
        <div className="py-12 text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
          <p className="mt-4 text-muted-foreground">Loading wallet details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 px-2 sm:px-0">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Card>
          <CardContent className="p-6">
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
              <h2 className="mb-2 font-bold">Error loading wallet details</h2>
              <p>{error.message || 'An unexpected error occurred'}</p>
              {error.response?.data && (
                <details className="mt-2 text-sm">
                  <summary className="cursor-pointer">Show technical details</summary>
                  <pre className="mt-2 overflow-auto rounded-md bg-muted p-2 text-xs text-foreground">
                    {JSON.stringify(error.response.data, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!wallet) return <div className="px-2 sm:px-0 text-muted-foreground">Wallet not found.</div>;

  const displayLedgerEntries = showAllLedger ? ledgerEntries.all : ledgerEntries.recent;

  return (
    <div className="space-y-6 px-2 sm:px-0">
      <PageHeader
        title="Wallet Details"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back
            </Button>
            {!isEditing && wallet && (
              <Button onClick={handleEdit}>Edit</Button>
            )}
          </div>
        }
      />

      {updateError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {updateError}
        </div>
      )}

      {isEditing ? (
        <Card>
          <CardHeader>
            <CardTitle>Edit wallet</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Wallet ID</p>
                <p className="font-mono text-sm">{wallet.id}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">User ID</p>
                <p className="font-mono text-sm">{wallet.user_id}</p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editForm.status || undefined}
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, status: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editForm.status === 'suspended' && (
                <div className="space-y-2">
                  <Label htmlFor="suspension_reason">Suspension Reason</Label>
                  <Textarea
                    id="suspension_reason"
                    name="suspension_reason"
                    value={editForm.suspension_reason}
                    onChange={(e) => setEditForm(prev => ({ ...prev, suspension_reason: e.target.value }))}
                    rows={3}
                    placeholder="Enter reason for suspension..."
                    required
                  />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel} disabled={updating}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border-primary/20 bg-primary text-primary-foreground">
            <CardContent className="p-6">
              <p className="mb-1 text-sm opacity-90">Current Balance</p>
              <p className="text-4xl font-bold">{formatCurrency(balance)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Wallet Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Wallet ID</p>
                  <p className="break-all font-mono text-sm">{wallet.id}</p>
                </div>
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">User ID</p>
                  <p className="break-all font-mono text-sm">{wallet.user_id}</p>
                </div>
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Status</p>
                  <Badge variant={wallet.status === 'active' ? 'success' : 'destructive'}>
                    {wallet.status}
                  </Badge>
                </div>
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Created At</p>
                  <p className="text-sm">{formatDate(wallet.created_at)}</p>
                </div>
                {wallet.suspended_at && (
                  <div>
                    <p className="mb-1 text-sm text-muted-foreground">Suspended At</p>
                    <p className="text-sm">{formatDate(wallet.suspended_at)}</p>
                  </div>
                )}
                {wallet.suspension_reason && (
                  <div className="md:col-span-2">
                    <p className="mb-1 text-sm text-muted-foreground">Suspension Reason</p>
                    <p className="text-sm">{wallet.suspension_reason}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {statistics && (
            <Card>
              <CardHeader>
                <CardTitle>Wallet Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Total Ledger Entries</p>
                    <p className="text-2xl font-bold">{statistics.totalLedgerEntries}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Total Credit</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(statistics.totalCredit)}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Total Debit</p>
                    <p className="text-2xl font-bold text-destructive">
                      {formatCurrency(statistics.totalDebit)}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Package Balance</p>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(statistics.totalPackageBalance)}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Total Packages</p>
                    <p className="text-2xl font-bold">{statistics.totalPackages}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Active Packages</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {statistics.activePackages}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-1 text-sm text-muted-foreground">Expired Packages</p>
                    <p className="text-2xl font-bold">{statistics.expiredPackages}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Ledger Entries</CardTitle>
                <CardDescription>{ledgerEntries.total} total</CardDescription>
              </div>
              {ledgerEntries.all.length > ledgerEntries.recent.length && (
                <Button
                  variant="link"
                  className="h-auto p-0"
                  onClick={() => setShowAllLedger(!showAllLedger)}
                >
                  {showAllLedger ? 'Show Recent Only' : `Show All (${ledgerEntries.all.length})`}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {displayLedgerEntries.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No ledger entries found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Balance After</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayLedgerEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-mono text-sm">
                          {entry.id ? String(entry.id).substring(0, 8) : '-'}...
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              entry.type === 'credit' || entry.entry_type === 'credit'
                                ? 'success'
                                : 'destructive'
                            }
                          >
                            {entry.type || entry.entry_type || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          <span className={entry.amount < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}>
                            {entry.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(entry.amount || 0))}
                          </span>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(entry.balance_after || entry.balanceAfter || 0)}
                        </TableCell>
                        <TableCell>
                          {formatDate(entry.created_at || entry.createdAt || entry.date)}
                        </TableCell>
                        <TableCell>
                          {entry.description || entry.reference || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User Packages</CardTitle>
              <CardDescription>
                {packages.total} total, {packages.active} active, {packages.expired} expired
              </CardDescription>
            </CardHeader>
            <CardContent>
              {packages.all.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No packages found</p>
              ) : (
                <div className="space-y-4">
                  {packages.all.map((userPackage) => {
                    const pkg = userPackage.package || {};
                    const packageTransactions = Array.isArray(userPackage.transactions) ? userPackage.transactions : [];
                    const isExpanded = expandedPackages.has(userPackage.id);
                    const isActive = pkg.status === 'active' || 
                      (pkg.valid_until && new Date(pkg.valid_until) > new Date()) ||
                      (pkg.validUntil && new Date(pkg.validUntil) > new Date());
                    
                    return (
                      <div key={userPackage.id} className="overflow-hidden rounded-lg border">
                        <button
                          type="button"
                          className="w-full cursor-pointer bg-muted/40 p-4 text-start hover:bg-muted/70"
                          onClick={() => togglePackageExpansion(userPackage.id)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1">
                              <h3 className="text-lg font-bold">{pkg.name || 'Unnamed Package'}</h3>
                              <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span>Price: {formatCurrency(pkg.price)}</span>
                                {pkg.discount_percentage && (
                                  <span>Discount: {pkg.discount_percentage}%</span>
                                )}
                                <span>Transactions: {packageTransactions.length}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant={isActive ? 'success' : 'secondary'}>
                                {isActive ? 'Active' : 'Expired'}
                              </Badge>
                              <span className="text-primary">{isExpanded ? '▼' : '▶'}</span>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {pkg.valid_from && (
                              <span>Valid from: {formatDate(pkg.valid_from || pkg.validFrom)}</span>
                            )}
                            {pkg.valid_until && (
                              <span className="ms-4">Valid until: {formatDate(pkg.valid_until || pkg.validUntil)}</span>
                            )}
                          </div>
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t p-4">
                            {pkg.description && (
                              <p className="mb-4 text-sm text-muted-foreground">{pkg.description}</p>
                            )}
                            
                            {packageTransactions.length > 0 ? (
                              <div>
                                <h4 className="mb-3 font-semibold">
                                  Package Transactions ({packageTransactions.length})
                                </h4>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>ID</TableHead>
                                      <TableHead>Type</TableHead>
                                      <TableHead>Amount</TableHead>
                                      <TableHead>Date</TableHead>
                                      <TableHead>Status</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {packageTransactions.map((tx) => (
                                      <TableRow key={tx.id}>
                                        <TableCell className="font-mono text-xs">
                                          {tx.id ? String(tx.id).substring(0, 8) : '-'}...
                                        </TableCell>
                                        <TableCell>
                                          <Badge variant="secondary">
                                            {tx.type || tx.transaction_type || '-'}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="font-semibold">
                                          <span className={tx.amount < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}>
                                            {tx.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(tx.amount || 0))}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          {formatDate(tx.created_at || tx.date)}
                                        </TableCell>
                                        <TableCell>
                                          <Badge
                                            variant={
                                              tx.status === 'success' || tx.status === 'completed'
                                                ? 'success'
                                                : 'warning'
                                            }
                                          >
                                            {tx.status || 'pending'}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No transactions for this package</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default WalletDetails;
